import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { fileAPI } from '../services/api';
import { getLang } from './LanguageToggle';
import './FileUpload.css';

// Lower-case because axios normalizes response header names. Mirrors
// ERROR_CODE_HEADER in backend/app/utils/filenames.py.
const ERROR_CODE_HEADER = 'x-prism-error-code';

const FU_TRANSLATIONS = {
  en: {
    upload_title: 'Upload Document',
    uploaded_title: 'Uploaded File',
    click_to_upload: 'Click to upload',
    or_drag: 'or drag and drop',
    pdf_hint: 'Please upload only one patient-specific clinical document at a time. PRISM does not accept guidance documents or research papers here, but you can request to add them via Help',
    uploading: 'Uploading...',
    processing: 'Processing...',
    stage_upload: 'Uploading file...',
    stage_scan: 'Scanning for threats...',
    stage_extract: 'Extracting text...',
    stage_pii: 'Masking sensitive information...',
    stage_index: 'Indexing for retrieval...',
    supported: 'Supported:',
    max_size: 'Max size:',
    privacy: 'Privacy:',
    privacy_value: 'Files are scanned for PII and automatically deleted after processing',
    ready: 'Ready for questions',
    note: 'You can now ask questions about this document. The AI will use both general medical knowledge and your specific file data.',
    remove: 'Remove file',
    manage_title: 'File uploaded — click to manage',
    upload_doc_title: 'Upload document',
    // Shown when the server refused to analyse an image because it could not
    // guarantee that personal information had been masked first. Not a generic
    // failure: the point is that nothing was sent for analysis.
    err_pii_redaction_unavailable:
      'PRISM could not verify that personal information in this image was hidden before analysis, so it stopped processing the file to protect patient privacy. Nothing was sent for analysis. Please try again in a few minutes, or upload a clearer image or a text-based PDF.',
    // Filename rejections from the server. It returns a stable code in
    // ERROR_CODE_HEADER so these can be localized; the English ``detail``
    // in the response body is the fallback when no code is present.
    err_filename_too_long: 'File name is too long. Please rename the file and try again.',
    err_filename_charset: 'File name contains characters PRISM cannot accept (for example # or %). Please rename the file and try again.',
    err_filename_invalid: 'File name is not valid. Please rename the file and try again.',
    err_filename_required: 'This file has no name. Please rename it and try again.',
    err_generic_prefix: 'Upload failed: ',
  },
  fr: {
    upload_title: 'Téléverser un document',
    uploaded_title: 'Fichier téléversé',
    click_to_upload: 'Cliquez pour téléverser',
    or_drag: 'ou glisser-déposer',
    pdf_hint: 'Veuillez téléverser un seul document clinique propre au patient à la fois. PRISM n’accepte pas les documents d’orientation ou les articles de recherche ici, mais vous pouvez demander à les ajouter via l’Aide.',
    uploading: 'Téléversement...',
    processing: 'Traitement en cours...',
    stage_upload: 'Téléversement du fichier...',
    stage_scan: 'Analyse antivirus...',
    stage_extract: 'Extraction du texte...',
    stage_pii: 'Masquage des informations sensibles...',
    stage_index: 'Indexation pour la recherche...',
    supported: 'Supporté :',
    max_size: 'Taille max :',
    privacy: 'Confidentialité :',
    privacy_value: 'Les fichiers sont analysés pour les RPS et automatiquement supprimés après traitement',
    ready: 'Prêt pour les questions',
    note: "Vous pouvez maintenant poser des questions sur ce document. L'IA utilisera les connaissances médicales générales et les données de votre fichier.",
    remove: 'Supprimer le fichier',
    manage_title: 'Fichier téléversé — cliquez pour gérer',
    upload_doc_title: 'Téléverser un document',
    err_pii_redaction_unavailable:
      "PRISM n'a pas pu confirmer que les renseignements personnels de cette image avaient été masqués avant l'analyse; le traitement du fichier a donc été arrêté afin de protéger la confidentialité du patient. Rien n'a été transmis pour analyse. Veuillez réessayer dans quelques minutes, ou téléverser une image plus nette ou un PDF textuel.",
    err_filename_too_long: 'Le nom du fichier est trop long. Veuillez le renommer et réessayer.',
    err_filename_charset: 'Le nom du fichier contient des caractères que PRISM n’accepte pas (par exemple # ou %). Veuillez le renommer et réessayer.',
    err_filename_invalid: 'Le nom du fichier n’est pas valide. Veuillez le renommer et réessayer.',
    err_filename_required: 'Ce fichier n’a pas de nom. Veuillez le renommer et réessayer.',
    err_generic_prefix: 'Échec du téléversement : ',
  },
};

/**
 * FileUpload is a CONTROLLED component — the parent (Chat.js) owns the
 * uploadedFile state and passes it down via the `uploadedFile` prop.
 *
 * Props:
 *   uploadedFile   – current file object from parent (null = no file)
 *   onUploadSuccess(result) – called when upload completes successfully
 *   onUploadError(message)  – called when upload fails
 *   onClearFile()           – called when file is removed (parent handles cleanup + state reset)
 *   disabled                – disable the upload button
 *
 * Ref API (via forwardRef):
 *   triggerModal() – open the upload modal programmatically
 */
const FileUpload = forwardRef(({ onUploadSuccess, onUploadError, onClearFile, uploadedFile, disabled }, ref) => {
  useImperativeHandle(ref, () => ({
    triggerModal: () => setShowUploadModal(true),
  }));
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStageIdx, setProcessingStageIdx] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Error shown INSIDE the modal. The parent also renders a banner in the chat
  // area, but while the upload modal is open that banner sits behind the modal
  // overlay (z-index 1000) and is unreadable — so we surface the same message
  // here, on top of the modal, where the user is actually looking.
  const [localError, setLocalError] = useState('');
  const fileInputRef = useRef(null);
  const processingTimerRef = useRef(null);

  // Once the HTTP upload finishes the server still has to scan, extract, mask
  // PII, embed, and write to Turbopuffer — for an image that's 10-30 s of
  // silence. Rotate through a labelled "Processing..." stage so the user
  // sees activity instead of a frozen 100% bar.
  const PROCESSING_STAGES = ['stage_scan', 'stage_extract', 'stage_pii', 'stage_index'];
  const PROCESSING_STAGE_MS = 2500;

  useEffect(() => {
    if (uploading && uploadProgress >= 100) {
      setProcessingStageIdx(0);
      processingTimerRef.current = setInterval(() => {
        setProcessingStageIdx(i => Math.min(i + 1, PROCESSING_STAGES.length - 1));
      }, PROCESSING_STAGE_MS);
    }
    return () => {
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading, uploadProgress >= 100]);

  const lang = getLang();
  const ft = FU_TRANSLATIONS[lang] || FU_TRANSLATIONS.en;

  const supportedTypes = ['.pdf', '.jpg', '.jpeg', '.png'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (file.size > maxFileSize) {
      return lang === 'fr' ? 'La taille du fichier doit être inférieure à 10 Mo' : 'File size must be less than 10MB';
    }
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    if (!supportedTypes.includes(extension)) {
      return lang === 'fr'
        ? `Type de fichier non supporté. Types autorisés : ${supportedTypes.join(', ')}`
        : `File type not supported. Allowed types: ${supportedTypes.join(', ')}`;
    }
    return null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled || uploading) return;
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      // Keep the modal open so the user sees the upload / processing status.
      // The modal automatically rerenders into the "Uploaded File" state once
      // the upload completes, and the user can close it manually.
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    setLocalError('');
    const validationError = validateFile(file);
    if (validationError) {
      setLocalError(validationError);
      if (onUploadError) onUploadError(validationError);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await fileAPI.upload(file, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(progress);
      });

      if (result.status === 'completed' && onUploadSuccess) {
        onUploadSuccess({ name: file.name, size: file.size, ...result });
      } else if (result.status === 'error') {
        // Coded errors get a localized message; anything else falls back to the
        // server's English text so no failure is ever swallowed.
        const coded = result.error_code
          ? ft['err_' + result.error_code]
          : null;
        const msg = coded || (ft.err_generic_prefix + result.message);
        setLocalError(msg);
        if (onUploadError) onUploadError(msg);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail || 'Upload failed';
      // Filename rejections carry a stable code so the user sees the message
      // in their own language; everything else falls back to the server text.
      const errorCode = error.response?.headers?.[ERROR_CODE_HEADER];
      const localized = errorCode ? ft['err_' + errorCode] : null;
      const isSecurityThreat =
        typeof detail === 'string' &&
        (detail.toLowerCase().includes('threat') ||
          detail.toLowerCase().includes('not safe') ||
          detail.toLowerCase().includes('security'));
      const shown = localized || (typeof detail === 'string' ? detail : 'Upload failed');
      setLocalError(shown);
      if (onUploadError) onUploadError(shown, isSecurityThreat);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = () => {
    setShowUploadModal(false);
    if (onClearFile) onClearFile();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // ── Render: file already uploaded ─────────────────────────────────────────
  if (uploadedFile && uploadedFile.status === 'completed') {
    return (
      <>
        <button
          type="button"
          className="upload-icon-btn active"
          onClick={() => setShowUploadModal(true)}
          disabled={disabled}
          title={ft.manage_title}
        >
          📎
        </button>

        {showUploadModal && (
          <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
            <div className="upload-modal" onClick={e => e.stopPropagation()}>
              <div className="upload-modal-header">
                <h3>{ft.uploaded_title}</h3>
                <button className="modal-close-btn" onClick={() => setShowUploadModal(false)}>✕</button>
              </div>

              <div className="uploaded-file-info">
                <div className="file-icon">📄</div>
                <div className="file-details">
                  <div className="file-name">{uploadedFile.name}</div>
                  <div className="file-size">{formatFileSize(uploadedFile.size || 0)}</div>
                  <div className="file-status">✅ {ft.ready}</div>
                </div>
                <button className="remove-file-btn" onClick={handleRemoveFile} title={ft.remove}>✕</button>
              </div>

              <div className="file-upload-note">
                {ft.note}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── Render: no file / upload form ─────────────────────────────────────────
  return (
    <>
      <button
        type="button"
        className={`upload-icon-btn ${uploading ? 'uploading' : ''}`}
        onClick={() => { setLocalError(''); setShowUploadModal(true); }}
        disabled={disabled || uploading}
        title={ft.upload_doc_title}
      >
        {uploading ? '⏳' : '📎'}
      </button>

      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal" onClick={e => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3>{ft.upload_title}</h3>
              <button className="modal-close-btn" onClick={() => setShowUploadModal(false)}>✕</button>
            </div>

            <div
              className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${disabled ? 'disabled' : ''} ${uploading ? 'uploading' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={supportedTypes.join(',')}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={disabled || uploading}
              />

              {uploading ? (
                <div className="upload-progress">
                  <div className="upload-icon">⏳</div>
                  <div className="upload-text">
                    {uploadProgress < 100
                      ? ft.uploading
                      : ft[PROCESSING_STAGES[processingStageIdx]] || ft.processing}
                  </div>
                  <div className={`progress-bar ${uploadProgress >= 100 ? 'indeterminate' : ''}`}>
                    <div
                      className="progress-fill"
                      style={uploadProgress >= 100 ? undefined : { width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="progress-text">
                    {uploadProgress < 100 ? `${uploadProgress}%` : ft.processing}
                  </div>
                </div>
              ) : (
                <div className="upload-prompt">
                  <div className="upload-icon">📁</div>
                  <div className="upload-text">
                    <strong>{ft.click_to_upload}</strong> {ft.or_drag}
                  </div>
                  <div className="upload-subtext">{ft.pdf_hint}</div>
                </div>
              )}
            </div>

            {localError && (
              <div className="upload-modal-error" role="alert">
                <span className="upload-modal-error-icon" aria-hidden="true">⚠️</span>
                <span className="upload-modal-error-text">{localError}</span>
                <button
                  type="button"
                  className="upload-modal-error-close"
                  onClick={() => setLocalError('')}
                  aria-label="✕"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="file-upload-info">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">📋 {ft.supported}</span>
                  <span className="info-value">{supportedTypes.join(', ')}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">📏 {ft.max_size}</span>
                  <span className="info-value">10MB</span>
                </div>
                <div className="info-item privacy-info">
                  <span className="info-label">🔒 {ft.privacy}</span>
                  <span className="info-value">{ft.privacy_value}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
